'use client';

// Fase 7 Bloco G · Cookie banner LGPD (renderizado no root layout · só aparece
// pra quem nunca decidiu).
//
// 4 categorias: Essencial (sempre on · não toggle) · Funcional · Analítico · Marketing.
// 3 ações: "Aceitar todos", "Personalizar" (mostra toggles), "Rejeitar não-essenciais".
//
// Persiste em cookie `_trakd_cookie_consent` (1 ano TTL · veja lib/cookies/consent.ts).

import { useEffect, useState, type ReactElement } from 'react';
import { TbCookie, TbX } from 'react-icons/tb';

import { acceptAll, getConsent, rejectNonEssential, setConsent } from '@/lib/cookies/consent';

type Category = 'functional' | 'analytics' | 'marketing';

const CATEGORIES: Array<{
  key: Category;
  title: string;
  description: string;
}> = [
  {
    key: 'functional',
    title: 'Funcionais',
    description: 'Lembram suas preferências (sidebar, tema, idioma).',
  },
  {
    key: 'analytics',
    title: 'Analíticos',
    description: 'Análise agregada de uso pra entender como o produto está sendo usado.',
  },
  {
    key: 'marketing',
    title: 'Marketing',
    description: 'Meta Pixel auxiliar pra remarketing institucional.',
  },
];

export function CookieBanner(): ReactElement | null {
  const [open, setOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [choices, setChoices] = useState<Record<Category, boolean>>({
    functional: true,
    analytics: true,
    marketing: true,
  });

  useEffect(() => {
    setMounted(true);
    const existing = getConsent();
    if (!existing) {
      // Delay pequeno pra evitar flash em primeira renderização
      const t = window.setTimeout(() => setOpen(true), 250);
      return () => window.clearTimeout(t);
    }
    return undefined;
  }, []);

  if (!mounted || !open) return null;

  function toggle(key: Category): void {
    setChoices((c) => ({ ...c, [key]: !c[key] }));
  }

  function handleAcceptAll(): void {
    acceptAll();
    setOpen(false);
  }

  function handleRejectAll(): void {
    rejectNonEssential();
    setOpen(false);
  }

  function handleSavePreferences(): void {
    setConsent(choices);
    setOpen(false);
  }

  return (
    <div
      role="dialog"
      aria-modal="false"
      aria-labelledby="cookie-banner-title"
      className="fixed inset-x-3 bottom-3 z-[100] mx-auto max-w-3xl sm:inset-x-6 sm:bottom-6"
    >
      <div className="border-border-subtle bg-surface/95 ring-violet/10 relative overflow-hidden rounded-2xl border shadow-2xl ring-1 backdrop-blur-xl">
        {/* Glow decorativo */}
        <div className="bg-violet/5 pointer-events-none absolute -top-20 -right-20 h-40 w-40 rounded-full blur-3xl" />
        <div className="bg-fuchsia/5 pointer-events-none absolute -bottom-20 -left-20 h-40 w-40 rounded-full blur-3xl" />

        <div className="relative p-5 sm:p-6">
          <div className="flex items-start gap-3">
            <span className="bg-violet/10 text-violet flex h-10 w-10 shrink-0 items-center justify-center rounded-xl">
              <TbCookie size={20} strokeWidth={1.7} />
            </span>
            <div className="min-w-0 flex-1">
              <h2 id="cookie-banner-title" className="text-ink text-base font-semibold">
                Cookies e privacidade
              </h2>
              <p className="text-ink-muted mt-1 text-sm leading-relaxed">
                A gente usa cookies pra fazer o produto funcionar (essenciais) e, com sua permissão,
                pra melhorar a experiência. Você decide.
              </p>
            </div>
            <button
              type="button"
              onClick={handleRejectAll}
              aria-label="Fechar banner (rejeita não-essenciais)"
              className="text-ink-muted hover:text-ink hover:bg-surface-elevated/60 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg transition-colors"
            >
              <TbX size={16} strokeWidth={1.7} />
            </button>
          </div>

          {expanded && (
            <div className="border-border-subtle/60 mt-4 space-y-3 border-t pt-4">
              <div className="border-border-subtle/60 flex items-start justify-between gap-3 rounded-xl border px-3 py-2.5">
                <div>
                  <p className="text-ink text-sm font-medium">Essenciais</p>
                  <p className="text-ink-faint text-xs">Autenticação e segurança. Sempre ativos.</p>
                </div>
                <span className="text-success bg-success/10 border-success/30 rounded-full border px-2.5 py-0.5 text-[10px] font-medium tracking-wider uppercase">
                  Sempre on
                </span>
              </div>

              {CATEGORIES.map((c) => (
                <label
                  key={c.key}
                  className="hover:border-violet/40 border-border-subtle/60 flex cursor-pointer items-start justify-between gap-3 rounded-xl border px-3 py-2.5 transition-colors"
                >
                  <div>
                    <p className="text-ink text-sm font-medium">{c.title}</p>
                    <p className="text-ink-faint text-xs">{c.description}</p>
                  </div>
                  <span className="shrink-0 pt-0.5">
                    <Toggle checked={choices[c.key]} onChange={() => toggle(c.key)} />
                  </span>
                </label>
              ))}
            </div>
          )}

          <div className="mt-4 flex flex-wrap items-center gap-2">
            {!expanded ? (
              <>
                <button
                  type="button"
                  onClick={handleAcceptAll}
                  className="bg-violet hover:bg-violet/90 inline-flex items-center justify-center rounded-xl px-4 py-2 text-sm font-medium text-white shadow-[0_0_16px_oklch(0.62_0.22_295/0.35)] transition-all"
                >
                  Aceitar todos
                </button>
                <button
                  type="button"
                  onClick={() => setExpanded(true)}
                  className="border-border-subtle hover:border-violet/50 text-ink-muted hover:text-ink inline-flex items-center justify-center rounded-xl border px-4 py-2 text-sm font-medium transition-colors"
                >
                  Personalizar
                </button>
                <button
                  type="button"
                  onClick={handleRejectAll}
                  className="text-ink-muted hover:text-ink inline-flex items-center justify-center rounded-xl px-4 py-2 text-sm font-medium transition-colors"
                >
                  Rejeitar não-essenciais
                </button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  onClick={handleSavePreferences}
                  className="bg-violet hover:bg-violet/90 inline-flex items-center justify-center rounded-xl px-4 py-2 text-sm font-medium text-white shadow-[0_0_16px_oklch(0.62_0.22_295/0.35)] transition-all"
                >
                  Salvar preferências
                </button>
                <button
                  type="button"
                  onClick={handleAcceptAll}
                  className="border-border-subtle hover:border-violet/50 text-ink-muted hover:text-ink inline-flex items-center justify-center rounded-xl border px-4 py-2 text-sm font-medium transition-colors"
                >
                  Aceitar todos
                </button>
              </>
            )}
            <a
              href="/legal/privacidade"
              target="_blank"
              rel="noopener noreferrer"
              className="text-ink-faint hover:text-ink-muted ml-auto text-xs underline-offset-2 hover:underline"
            >
              Política de privacidade
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

// --- subcomponente toggle ---------------------------------------------------

function Toggle({ checked, onChange }: { checked: boolean; onChange: () => void }): ReactElement {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={onChange}
      className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full border transition-colors ${
        checked ? 'bg-violet/30 border-violet/50' : 'bg-surface-elevated border-border-subtle'
      }`}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full transition-transform ${
          checked ? 'bg-violet translate-x-[18px]' : 'bg-ink-muted translate-x-0.5'
        }`}
      />
    </button>
  );
}
